import React, { useState, useEffect, useRef, useCallback } from "react";
import { db } from "./firebaseConfig";
import { collection, query, where, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./ButtonStyles.css";

const EnterQuiz = ({ user }) => {
  const [quizCode, setQuizCode] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [savedQuestions, setSavedQuestions] = useState({});
  // State to track the remaining time (in seconds)
  const [remainingTime, setRemainingTime] = useState(null);
  // State to indicate when a quiz is successfully loaded so we can start the timer once
  const [quizLoaded, setQuizLoaded] = useState(false);
  // New state to track if the quiz is locked
  const [quizLocked, setQuizLocked] = useState(false);
  // State to track submission completion
  const [submissionComplete, setSubmissionComplete] = useState(false);
  // State to track redirect countdown
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const navigate = useNavigate();
  // Ref to ensure the timer interval is started only once
  const timerStarted = useRef(false);
  // Ref to store the timer interval ID so we can cancel it if needed
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    console.log("User in EnterQuiz component:", user);
  }, [user]);

  const loadQuiz = async () => {
    if (!quizCode) {
      alert("Please enter a Quiz Code!");
      return;
    }

    try {
      const quizzesRef = collection(db, "quizzes");
      const quizQuery = query(quizzesRef, where("quizCode", "==", quizCode));
      const quizSnapshot = await getDocs(quizQuery);

      if (!quizSnapshot.empty) {
        const quizData = quizSnapshot.docs[0].data();

        // Check if the quiz is locked. If so, mark it and stop further processing.
        if (quizData.locked) {
          setQuizLocked(true);
          return;
        }

        // Adjust the timer based on the defaultTimeLimit (in seconds) and the student's extraTimePercentage.
        if (quizData.defaultTimeLimit) {
          // Fetch the current user's document to retrieve extraTimePercentage.
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          let extraTimePercentage = 0;
          if (userDocSnap.exists()) {
            extraTimePercentage = userDocSnap.data().extraTimePercentage || 0;
          }
          // Calculate adjusted time.
          const adjustedTime = Math.round(
            quizData.defaultTimeLimit * (1 + extraTimePercentage / 100)
          );
          setRemainingTime(adjustedTime);
        } else {
          setRemainingTime(0);
        }

        const questionIds = quizData.questionIds || [];
        const questionsCollection = collection(db, "questions");
        const questionsQuery = await getDocs(questionsCollection);
        
        // First get all the question documents that match our IDs
        const questionDocs = questionsQuery.docs.filter((doc) => 
          questionIds.includes(doc.id)
        );
        
        // Then map them to objects and sort them according to the order in questionIds
        const fetchedQuestions = questionIds.map(id => {
          const docMatch = questionDocs.find(doc => doc.id === id);
          if (docMatch) {
            return {
              id: docMatch.id,
              ...docMatch.data()
            };
          }
          return null;
        }).filter(Boolean); // Remove any null values (in case a question was deleted)

        setQuizQuestions(fetchedQuestions);
        setQuizLoaded(true);
      } else {
        alert("Quiz not found. Please check the Quiz Code.");
      }
    } catch (error) {
      console.error("Error loading quiz:", error);
      alert("Failed to load quiz. Please try again.");
    }
  };

  // Memoize event handlers to prevent unnecessary re-renders
  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  // Create a common submission document creator function to reduce code duplication
  const createSubmissionDoc = useCallback((questionId, answer) => {
    if (!user?.uid || !quizCode) return null;
    
    return {
      submissionId: `${user.uid}_${quizCode}_${questionId}`,
      questionId: `/questions/${questionId}`,
      userAnswer: answer,
      submittedAt: new Date(),
      feedback: "",
      userId: user.uid,
      quizCode,
    };
  }, [user, quizCode]);

  // Regular manual answer saving - now stores blank answers explicitly.
  const saveAnswer = useCallback(async (questionId) => {
    const formattedAnswer =
      answers[questionId] && answers[questionId].trim() !== ""
        ? answers[questionId]
        : "Question not answered by student";

    if (!user?.uid) {
      alert("User is not logged in. Please log in and try again.");
      return;
    }

    try {
      const submissionId = `${user.uid}_${quizCode}_${questionId}`;
      const submissionDoc = doc(db, "submissions", submissionId);
      const docData = createSubmissionDoc(questionId, formattedAnswer);
      if (!docData) return;
      
      await setDoc(submissionDoc, docData);
      setSavedQuestions((prev) => ({ ...prev, [questionId]: true }));
    } catch (error) {
      console.error("Error saving answer:", error);
      alert("Failed to save answer. Please try again.");
    }
  }, [user, quizCode, answers, createSubmissionDoc]);

  // Optimized auto-save function
  const saveAnswerAuto = useCallback(async (questionId, autoText = "Question not answered by student") => {
    if (!user?.uid) {
      console.error("User is not logged in.");
      return;
    }
    
    try {
      const submissionId = `${user.uid}_${quizCode}_${questionId}`;
      const submissionDoc = doc(db, "submissions", submissionId);
      const docData = createSubmissionDoc(questionId, autoText);
      if (!docData) return;
      
      await setDoc(submissionDoc, docData);
      setSavedQuestions((prev) => ({ ...prev, [questionId]: true }));
      
      // Only update the answers state if we're setting it to the default text
      // to avoid overwriting what the user typed
      if (autoText === "Question not answered by student") {
        setAnswers((prev) => ({ ...prev, [questionId]: autoText }));
      }
    } catch (error) {
      console.error("Error auto-saving answer:", error);
    }
  }, [user, quizCode, createSubmissionDoc]);

  // Memoize helper function to format time as mm:ss
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Timer effect: Start countdown when quizLoaded becomes true
  useEffect(() => {
    if (quizLoaded && remainingTime !== null && !timerStarted.current) {
      timerStarted.current = true;
      timerIntervalRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerIntervalRef.current);
    }
  }, [quizLoaded]); // Removed remainingTime from dependency array

  // Optimize the beforeunload event handler
  useEffect(() => {
    // Only add the event listener if the quiz is loaded
    if (quizLoaded && quizQuestions.length > 0) {
      // Create a synchronous version of the handler for better browser compatibility
      const handleBeforeUnload = (event) => {
        // Standard way to show a confirmation dialog
        event.preventDefault();
        event.returnValue = ""; // Chrome requires returnValue to be set
        
        // Use a synchronous approach to save answers
        // We'll create a flag in localStorage to indicate we need to save on next load
        const unsavedQuestions = quizQuestions
          .filter(question => !savedQuestions[question.id])
          .map(question => {
            const currentAnswer = answers[question.id] || "";
            return {
              questionId: question.id,
              answer: currentAnswer.trim() === "" ? 
                "Question not answered by student" : currentAnswer
            };
          });
        
        if (unsavedQuestions.length > 0) {
          try {
            // Store the data that needs to be saved
            localStorage.setItem(
              `quiz_${quizCode}_${user?.uid}_pending_saves`, 
              JSON.stringify(unsavedQuestions)
            );
            
            // We'll use a separate useEffect to check for and process this data on component mount
          } catch (error) {
            console.error("Error storing pending saves:", error);
          }
        }
      };

      // Add the event listener
      window.addEventListener("beforeunload", handleBeforeUnload);
      
      // Clean up the event listener when the component unmounts
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [quizLoaded, quizQuestions, savedQuestions, answers, quizCode, user]);

  // Handle pending saves from previous unload
  useEffect(() => {
    const processPendingSaves = async () => {
      if (!user?.uid || !quizCode || !quizLoaded) return;
      
      const pendingSavesKey = `quiz_${quizCode}_${user.uid}_pending_saves`;
      const pendingSavesJson = localStorage.getItem(pendingSavesKey);
      
      if (pendingSavesJson) {
        try {
          const pendingSaves = JSON.parse(pendingSavesJson);
          
          // Process all pending saves
          const savePromises = pendingSaves.map(item => 
            saveAnswerAuto(item.questionId, item.answer)
          );
          
          await Promise.all(savePromises);
          
          // Clear the pending saves
          localStorage.removeItem(pendingSavesKey);
        } catch (error) {
          console.error("Error processing pending saves:", error);
        }
      }
    };
    
    processPendingSaves();
  }, [quizLoaded, quizCode, user, saveAnswerAuto]);

  // Extract common save logic to reduce code duplication
  const saveAllQuestions = useCallback(async () => {
    if (quizQuestions.length === 0) return;
    
    const autoSavePromises = quizQuestions.map((question) => {
      const currentAnswer = answers[question.id] || "";
      
      if (currentAnswer.trim() === "") {
        return saveAnswerAuto(question.id);
      } else {
        return saveAnswerAuto(question.id, currentAnswer);
      }
    });
    
    return Promise.all(autoSavePromises);
  }, [quizQuestions, answers, saveAnswerAuto]);

  // Optimized timer expiration handler
  useEffect(() => {
    if (remainingTime === 0 && quizQuestions.length > 0) {
      // Use the common save function
      saveAllQuestions()
        .then(() => {
          const timeoutId = setTimeout(() => {
            navigate("/dashboard");
          }, 5000);
          return () => clearTimeout(timeoutId);
        })
        .catch((error) => {
          console.error("Error auto-saving questions:", error);
          navigate("/dashboard");
        });
    }
  }, [remainingTime, quizQuestions.length, saveAllQuestions, navigate]);

  // Optimized submit & finish handler
  const handleSubmitFinish = useCallback(async () => {
    // Cancel the timer so it doesn't conflict with manual submission
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    try {
      // Use the common save function
      await saveAllQuestions();
      
      // Set submission complete to show the completion message
      setSubmissionComplete(true);
      
      // Start countdown for redirect
      const countdownInterval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            navigate("/dashboard");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error("Error during submission:", error);
      navigate("/dashboard");
    }
  }, [timerIntervalRef, saveAllQuestions, navigate]);

  // Enhanced styles for a more professional look
  const containerStyle = {
    background: "linear-gradient(135deg, #1d384d, #007442)",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#333",
  };

  const tileStyle = {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "40px",
    margin: "50px auto",
    maxWidth: "900px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    textAlign: "center",
    transition: "all 0.3s ease",
  };

  const headerStyle = {
    position: "relative",
    marginBottom: "30px",
    padding: "20px 0",
    borderBottom: "2px solid #f0f0f0",
  };

  const titleStyle = {
    margin: 0,
    position: "relative",
    textAlign: "center",
    color: "#1a2a6c",
    fontSize: "28px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  };

  const backButtonStyle = {
    position: "absolute",
    left: "20px",
    top: "50%",
    transform: "translateY(-50%)",
    padding: "10px 20px",
    backgroundColor: "#6c757d",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
    zIndex: 10,
  };

  const inputStyle = {
    marginLeft: "10px",
    padding: "10px 15px",
    borderRadius: "5px",
    border: "1px solid #ddd",
    fontSize: "16px",
    transition: "border-color 0.2s ease",
    boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
  };

  const loadButtonStyle = {
    marginLeft: "20px",
    marginTop: "10px",
    padding: "10px 20px",
    backgroundColor: "#1a2a6c",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  };

  const questionCardStyle = (isSaved) => ({
    marginBottom: "20px",
    border: "1px solid #eaeaea",
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: isSaved ? "#e8f4f8" : "#fff",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    transition: "all 0.3s ease",
  });

  const questionTextStyle = {
    fontSize: "18px",
    fontWeight: "500",
    marginBottom: "15px",
    color: "#333",
    lineHeight: "1.5",
    whiteSpace: "pre-wrap",
    textAlign: "left",
  };

  const textareaStyle = {
    width: "100%",
    padding: "12px 15px",
    marginTop: "15px",
    borderRadius: "5px",
    border: "1px solid #ddd",
    fontSize: "16px",
    resize: "vertical",
    minHeight: "120px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.1)",
    transition: "border-color 0.2s ease",
  };

  const saveButtonStyle = (isSaved) => ({
    padding: "8px 20px",
    backgroundColor: isSaved ? "#28a745" : "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  });

  const editButtonStyle = {
    marginLeft: "10px",
    padding: "8px 20px",
    backgroundColor: "#ffc107",
    color: "#212529",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontWeight: "500",
    transition: "background-color 0.2s ease",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
  };

  const submitButtonStyle = {
    padding: "12px 30px",
    backgroundColor: "#1a2a6c",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    fontSize: "18px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  };

  const timerStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "12px 20px",
    backgroundColor: "#1a2a6c",
    color: "#fff",
    borderRadius: "8px",
    fontSize: "18px",
    fontWeight: "bold",
    zIndex: 1000,
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const timerIconStyle = {
    marginRight: "8px",
    fontSize: "20px",
  };

  const completionMessageStyle = {
    textAlign: "center",
    padding: "40px",
  };

  const completionTitleStyle = {
    color: "#1a2a6c",
    fontSize: "32px",
    marginBottom: "20px",
    fontWeight: "600",
  };

  const completionTextStyle = {
    fontSize: "20px",
    marginTop: "20px",
    color: "#555",
  };

  // If the quiz is locked, display the locked message instead of loading the quiz.
  if (quizLocked) {
    return (
      <div style={containerStyle}>
        <div style={tileStyle}>
          <h1 style={{ color: "#1a2a6c", fontSize: "28px", marginBottom: "20px", fontWeight: "600" }}>
            This quiz is locked
          </h1>
          <p style={{ fontSize: "18px", marginBottom: "30px", color: "#555" }}>
            Please contact your teacher.
          </p>
          <button 
            onClick={() => navigate("/dashboard")} 
            style={{
              padding: "12px 25px",
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontWeight: "500",
              fontSize: "16px",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // If submission is complete, display the completion message
  if (submissionComplete) {
    return (
      <div style={containerStyle}>
        <div style={tileStyle}>
          <div style={completionMessageStyle}>
            <h1 style={completionTitleStyle}>Test completed!</h1>
            <div style={{ 
              width: "80px", 
              height: "80px", 
              margin: "0 auto", 
              backgroundColor: "#28a745", 
              borderRadius: "50%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)"
            }}>
              <span style={{ color: "white", fontSize: "40px" }}>✓</span>
            </div>
            <p style={completionTextStyle}>
              Redirecting to dashboard in {redirectCountdown} seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // If time is up, display the "Time is up!" page.
  if (remainingTime === 0) {
    return (
      <div style={containerStyle}>
        <div style={tileStyle}>
          <div style={completionMessageStyle}>
            <h1 style={completionTitleStyle}>Time is up!</h1>
            <div style={{ 
              width: "80px", 
              height: "80px", 
              margin: "0 auto", 
              backgroundColor: "#dc3545", 
              borderRadius: "50%", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center",
              boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)"
            }}>
              <span style={{ color: "white", fontSize: "40px" }}>⏱</span>
            </div>
            <p style={completionTextStyle}>
              You will be redirected to the dashboard in 5 seconds.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Fixed Timer that's always visible when quiz is loaded */}
      {remainingTime !== null && quizQuestions.length > 0 && (
        <div style={timerStyle}>
          <span style={timerIconStyle}>⏱</span>
          Time Remaining: {formatTime(remainingTime)}
        </div>
      )}

      <div style={tileStyle}>
        {/* Header */}
        <div style={headerStyle}>
          {/* Only show the Back to Dashboard button if quiz isn't loaded */}
          {!quizLoaded && (
            <button 
              onClick={() => navigate("/dashboard")} 
              style={backButtonStyle}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#5a6268"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#6c757d"}
            >
              Back to Dashboard
            </button>
          )}
          <h1 style={titleStyle}>AI Automarker - Enter Quiz</h1>
        </div>

        {/* Quiz Code Input - Only show if quiz is not loaded */}
        {!quizLoaded && (
          <div style={{ marginBottom: "30px" }}>
            <label 
              htmlFor="quizCode" 
              style={{ 
                fontSize: "18px", 
                fontWeight: "500", 
                marginRight: "10px",
                display: "block",
                marginBottom: "10px"
              }}
            >
              Enter Quiz Code:
            </label>
            <input
              id="quizCode"
              type="text"
              value={quizCode}
              onChange={(e) => setQuizCode(e.target.value)}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = "#1a2a6c"}
              onBlur={(e) => e.target.style.borderColor = "#ddd"}
              placeholder="Enter code here"
            />
            <button 
              onClick={loadQuiz} 
              style={loadButtonStyle}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#0f1a4c"}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#1a2a6c"}
            >
              Load Quiz
            </button>
          </div>
        )}

        {/* Quiz Questions */}
        <div style={{ marginTop: "30px" }}>
          {quizQuestions.length > 0 ? (
            quizQuestions.map((question, index) => (
              <div
                key={question.id}
                style={questionCardStyle(savedQuestions[question.id])}
              >
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  marginBottom: "15px",
                  borderBottom: "1px solid #eaeaea",
                  paddingBottom: "10px"
                }}>
                  <div style={{ 
                    backgroundColor: "#1a2a6c", 
                    color: "white", 
                    width: "36px", 
                    height: "36px", 
                    borderRadius: "50%", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    marginRight: "15px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    minWidth: "36px", // Ensure minimum width
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)" // Add subtle shadow for depth
                  }}>
                    {index + 1}
                  </div>
                  {question.questionImageURL ? (
                    <div style={{ textAlign: "center", width: "100%" }}>
                      <img
                        src={question.questionImageURL}
                        alt="Question"
                        style={{
                          maxWidth: "600px",
                          maxHeight: "600px",
                          width: "auto",
                          height: "auto",
                          display: "block",
                          margin: "15px auto",
                          borderRadius: "8px",
                          border: "1px solid #eaeaea",
                          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
                        }}
                      />
                    </div>
                  ) : (
                    <p style={questionTextStyle}>{question.questionText}</p>
                  )}
                </div>
                
                <textarea
                  rows="4"
                  value={answers[question.id] || ""}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  style={{
                    ...textareaStyle,
                    backgroundColor: savedQuestions[question.id] ? "#f8f9fa" : "#fff",
                    cursor: savedQuestions[question.id] ? "not-allowed" : "text"
                  }}
                  onFocus={(e) => e.target.style.borderColor = "#1a2a6c"}
                  onBlur={(e) => e.target.style.borderColor = "#ddd"}
                  placeholder="Type your answer here..."
                  readOnly={savedQuestions[question.id]}
                  disabled={savedQuestions[question.id]}
                />
                <div style={{ marginTop: "15px", display: "flex", justifyContent: "center" }}>
                  <button
                    onClick={() => saveAnswer(question.id)}
                    style={saveButtonStyle(savedQuestions[question.id])}
                    onMouseOver={(e) => {
                      const baseColor = savedQuestions[question.id] ? "#218838" : "#0069d9";
                      e.currentTarget.style.backgroundColor = baseColor;
                    }}
                    onMouseOut={(e) => {
                      const baseColor = savedQuestions[question.id] ? "#28a745" : "#007bff";
                      e.currentTarget.style.backgroundColor = baseColor;
                    }}
                  >
                    {savedQuestions[question.id] ? "✓ Saved" : "Save"}
                  </button>
                  {savedQuestions[question.id] && (
                    <button
                      onClick={() =>
                        setSavedQuestions((prev) => ({ ...prev, [question.id]: false }))
                      }
                      style={editButtonStyle}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#e0a800"}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#ffc107"}
                    >
                      Edit Answer
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={{ 
              padding: "40px 20px", 
              textAlign: "center", 
              color: "#666",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px",
              border: "1px dashed #ddd"
            }}>
              <p style={{ fontSize: "18px", marginBottom: "0" }}>No questions loaded yet.</p>
              <p style={{ fontSize: "16px", marginTop: "10px", color: "#888" }}>
                Enter a quiz code and click "Load Quiz" to begin.
              </p>
            </div>
          )}
        </div>

        {/* Submit & Finish Button (only when quiz is loaded) */}
        {quizLoaded && (
          <div style={{ marginTop: "40px", textAlign: "center" }}>
            <button
              onClick={handleSubmitFinish}
              style={submitButtonStyle}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#0f1a4c";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.15)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1a2a6c";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
              }}
            >
              Submit & Finish
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnterQuiz;
